import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import { ShellAvatar, ShellMenu } from "@bw/shared/components";
import type { NavPage } from "@bw/shared/navigation";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Divider from "@mui/material/Divider";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import { useColorScheme } from "@mui/material/styles";
import { useAuth } from "@bw/shared/hooks";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

function initials(label: string) {
  const words = label.trim().split(/\s+/).filter(Boolean);
  const letters =
    words.length > 1 ? `${words[0]![0]}${words[1]![0]}` : label.slice(0, 2);
  return letters.toUpperCase();
}

/**
 * The top-bar account menu: the user's own pages from the navigation
 * definition (My coaching) first, then workspace, theme and sign-out.
 */
export function NavUser<A>({
  pages = [],
  organizationLabel,
}: {
  pages?: readonly NavPage<A>[];
  /** e.g. "Owner · Northgate Badminton Co." */
  organizationLabel?: string | null;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { user, signOut } = useAuth();
  const { mode, setMode, systemMode } = useColorScheme();
  const navigate = useNavigate();
  const name = user?.displayName || user?.email?.split("@")[0] || "Account";
  const email = user?.email || "";
  const open = Boolean(anchorEl);
  const dark = mode === "dark" || (mode === "system" && systemMode === "dark");
  const close = () => setAnchorEl(null);

  const handleLogout = async () => {
    close();
    await signOut();
    navigate("/sign-in");
  };

  return (
    <>
      <ButtonBase
        type="button"
        aria-label={`Account menu for ${name}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{ borderRadius: "50%", flexShrink: 0, ml: 0.5 }}
      >
        <ShellAvatar src={user?.photoURL ?? undefined} alt="">
          {initials(name)}
        </ShellAvatar>
      </ButtonBase>

      <ShellMenu anchorEl={anchorEl} open={open} onClose={close}>
        <ListSubheader
          component="li"
          disableSticky
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            px: 1,
            pt: 1,
            pb: 1.25,
            lineHeight: 1.25,
            bgcolor: "transparent",
          }}
        >
          <Avatar
            src={user?.photoURL ?? undefined}
            alt=""
            sx={{ width: 34, height: 34, fontSize: 13 }}
          >
            {initials(name)}
          </Avatar>
          <Box sx={{ minWidth: 0, overflow: "hidden" }}>
            <Typography noWrap sx={{ fontSize: 13, fontWeight: 700, color: "text.primary" }}>
              {name}
            </Typography>
            {email ? (
              <Typography color="text.secondary" noWrap component="p" sx={{ fontSize: 12 }}>
                {email}
              </Typography>
            ) : null}
            {organizationLabel ? (
              <Typography color="text.secondary" noWrap component="p" sx={{ fontSize: 12 }}>
                {organizationLabel}
              </Typography>
            ) : null}
          </Box>
        </ListSubheader>
        <Divider component="li" />
        {pages.map((page) => {
          const PageIcon = page.icon;
          return (
            <MenuItem key={page.id} component={Link} to={page.path} onClick={close}>
              <ListItemIcon>
                <PageIcon />
              </ListItemIcon>
              <ListItemText>{page.label}</ListItemText>
            </MenuItem>
          );
        })}
        <MenuItem component={Link} to="/workspaces" onClick={close}>
          <ListItemIcon>
            <SwapHorizOutlinedIcon aria-hidden="true" />
          </ListItemIcon>
          <ListItemText>Switch workspace</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMode(dark ? "light" : "dark");
            close();
          }}
        >
          <ListItemIcon>
            {dark ? (
              <LightModeOutlinedIcon aria-hidden="true" />
            ) : (
              <DarkModeOutlinedIcon aria-hidden="true" />
            )}
          </ListItemIcon>
          <ListItemText>{dark ? "Light theme" : "Dark theme"}</ListItemText>
        </MenuItem>
        <Divider component="li" />
        <MenuItem onClick={() => void handleLogout()}>
          <ListItemIcon>
            <LogoutOutlinedIcon aria-hidden="true" />
          </ListItemIcon>
          <ListItemText>Log out</ListItemText>
        </MenuItem>
      </ShellMenu>
    </>
  );
}
